import { Outlet, createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useGetAllClients } from '@/gen/publicApi'

import Navigation from '@/containers/navigation'
import RequiresEmailVerification from '@/containers/requiresEmailVerification'

import Spinner from '@/components/spinner'
import { TooltipProvider } from '@/components/tooltip'

import { useAuthToken } from '@/state/authToken'
import { useClientCode, useSetClientCode } from '@/state/clientCode'

import { useIdentity } from '../state/identity'

export const Route = createFileRoute('/_authed')({
    component: AuthedLayoutComponent,
})

function AuthedLayoutComponent() {
    const { client_code } = useParams({
        from: '/_authed/$client_code/',
    })

    const setClientCode = useSetClientCode()
    const clientCode = useClientCode()
    const { data: clients, isLoading, isError } = useGetAllClients(undefined)
    const { identity } = useIdentity()
    const authToken = useAuthToken()
    const navigate = useNavigate()

    useEffect(() => {
        if (!authToken || !clients?.data?.length || isLoading) {
            return
        }

        if (isError) {
            console.error('Error getting clients', isError)
            setClientCode(undefined)
            navigate({
                to: '/',
            })
            return
        }

        const containsClient = clients.data.some(c => c.code.toLowerCase() === client_code?.toLowerCase())

        if (
            (!clientCode && !!client_code && containsClient) ||
            (!!client_code && clientCode !== client_code && containsClient)
        ) {
            console.debug('Setting client code', clientCode, client_code)
            // If the user has come straight to a route with a client code in the URL
            // then we might need to set it in the atom for later reference
            setClientCode(client_code)
            return
        }

        // If the client code is one that the user does not have access to, clear
        // it and redirect to the client selector
        if (client_code && !containsClient) {
            setClientCode(undefined)
            navigate({
                to: '/',
            })
        }
        /**
         * This check is only here to potentially redirect on first page load. We
         * don't want re-runs whenever any other client code changes (which may be
         * if they select a new one from the client swicther) as this code is only
         * here for a single initial check, not checking as state changes. No need
         * to have rerenders and code re-running when things change that we don't
         * care about.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, clients?.data, isLoading, isError])

    useEffect(() => {
        // Redirect to login if we don't have an auth token to validate. If the auth token
        // is cleared then this will also redirect to login.
        if (!authToken) {
            console.debug('No auth token, redirecting to login')
            navigate({
                to: '/login',
                search: { redirect: location.href },
            })
        }
    }, [navigate, authToken])

    if (!identity) {
        return <Spinner size="sm" absolute />
    }

    return (
        <>
            <RequiresEmailVerification />
            <div className="h-screen overflow-y-hidden">
                <div className="flex h-screen">
                    <Navigation />
                    <div className="flex-grow p-4 overflow-y-scroll">
                        <TooltipProvider>
                            <Outlet />
                        </TooltipProvider>
                    </div>
                </div>
            </div>
        </>
    )
}
